/**
 * Orquestrador Ecommerce: lista em /ecommerce e Gerenciar em sub-rota.
 * @module EcommercePage
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Skeleton } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import type { Patient } from "fhir/r5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import {
  buildEcommerceManagePath,
  ECOMMERCE_LIST_PATH,
  isEcommerceOrganization,
  useEcommerceWhatsAppEndpoint,
  type EcommerceManageLocationState,
} from "src-next/modules/ecommerce";
import { useEcommerceSendMessage } from "../hooks/useEcommerceSendMessage";
import { ManageDocumentsReleaseRoute } from "../ManageDocumentsRelease/ManageDocumentsReleaseRoute";
import { ManageDocumentsReleaseSkeleton } from "../ManageDocumentsRelease/ManageDocumentsReleaseSkeleton";
import {
  EcommerceListPage,
  EcommerceListPageSkeleton,
  EcommerceOrganizationUnavailable,
} from "./EcommerceListPage";

const WHATSAPP_CHIP_MIN_WIDTH = 210;

const EcommercePage = memo(function EcommercePage() {
  const { user } = useSessionContext();
  const { organization, organizationId, loading } = useOrganizationContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { "*": splat = "" } = useParams();
  const isManageRoute =
    splat === "gerenciar" ||
    splat.startsWith("gerenciar/") ||
    location.pathname.startsWith(`${ECOMMERCE_LIST_PATH}/gerenciar`);

  const [listRevision, setListRevision] = useState(0);
  const wasManageRouteRef = useRef(false);

  useEffect(() => {
    if (wasManageRouteRef.current && !isManageRoute) {
      setListRevision((revision) => revision + 1);
    }
    wasManageRouteRef.current = isManageRoute;
  }, [isManageRoute]);

  const whatsAppChannel = useEcommerceWhatsAppEndpoint(organization?.id);

  const { openSend, dialog: sendDialog } = useEcommerceSendMessage({
    organizationId,
    practitionerId: user?.username
      ? `${oids.cpf}-${user.username}`
      : undefined,
    hasActiveMetaEndpoint: whatsAppChannel.hasActiveMetaEndpoint,
  });

  const cnpjFromId = organization?.id?.split("-")?.[1];

  const handleOpenManage = useCallback(
    (patient: Patient, transactionId?: string, listSearch?: string) => {
      navigate(buildEcommerceManagePath(patient, transactionId), {
        state: {
          patient,
          listSearch: listSearch || location.search,
        } satisfies EcommerceManageLocationState,
      });
    },
    [navigate, location.search],
  );

  const handleCloseManage = useCallback(() => {
    const navigationState = location.state as
      | EcommerceManageLocationState
      | null;
    navigate({
      pathname: ECOMMERCE_LIST_PATH,
      search: navigationState?.listSearch || "",
    });
  }, [navigate, location.state]);

  const whatsAppStatusChip = useMemo(
    () => (
      <Box
        sx={{
          minWidth: WHATSAPP_CHIP_MIN_WIDTH,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {whatsAppChannel.isLoading ? (
          <Skeleton
            variant="rounded"
            width={WHATSAPP_CHIP_MIN_WIDTH}
            height={32}
            sx={{ borderRadius: 8 }}
          />
        ) : whatsAppChannel.hasActiveMetaEndpoint ? (
          <Chip
            size="small"
            icon={<WhatsAppIcon sx={{ color: "#25D366 !important" }} />}
            label="WhatsApp conectado"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600, minWidth: WHATSAPP_CHIP_MIN_WIDTH }}
          />
        ) : (
          <Chip
            size="small"
            icon={<WhatsAppIcon />}
            label="WhatsApp não conectado"
            variant="outlined"
            sx={{ fontWeight: 600, minWidth: WHATSAPP_CHIP_MIN_WIDTH }}
          />
        )}
      </Box>
    ),
    [whatsAppChannel.isLoading, whatsAppChannel.hasActiveMetaEndpoint],
  );

  const whatsAppChipSkeleton = (
    <Skeleton
      variant="rounded"
      width={WHATSAPP_CHIP_MIN_WIDTH}
      height={32}
      sx={{ borderRadius: 8 }}
    />
  );

  if (loading) {
    return isManageRoute ? (
      <ManageDocumentsReleaseSkeleton />
    ) : (
      <EcommerceListPageSkeleton action={whatsAppChipSkeleton} />
    );
  }

  if (!organization || !isEcommerceOrganization(organization)) {
    return <EcommerceOrganizationUnavailable />;
  }

  const listPage = (
    <EcommerceListPage
      organization={organization}
      whatsAppStatusChip={whatsAppStatusChip}
      whatsAppChannel={whatsAppChannel}
      listRevision={listRevision}
      onOpenManage={handleOpenManage}
      onSendWhatsApp={openSend}
    />
  );

  const managePage = (
    <ManageDocumentsReleaseRoute
      organizationId={cnpjFromId}
      onSendWhatsApp={openSend}
      onClose={handleCloseManage}
    />
  );

  return (
    <>
      {isManageRoute ? managePage : listPage}
      {sendDialog}
    </>
  );
});

export default EcommercePage;
